import type { ToastPayload } from "../../../context/ToastContext";
import type { IssueWorkspaceLaunchResponse } from "./types";

import { wakeBlockPresentation } from "../blackboardModels";

// ---------------------------------------------------------------------------
// Wake-block toast transition gate
//
// The dialog re-POSTs the launch every few seconds while in progress, so every
// poll yields a fresh snapshot object with identical content. The toast must
// fire on the TRANSITION into a block, not per response: dedupe on
// operationId + recipientInstanceId + wakeBlockReason.
//
// Kept as a pure reducer (previous key in, next key + payload out) so the
// dialog only owns the ref, and the dedupe itself is directly testable.
// ---------------------------------------------------------------------------

/** Stable identity of "this block, for this recipient, in this operation". */
export function wakeBlockToastKey(
  snapshot: IssueWorkspaceLaunchResponse,
): string | null {
  const { openingContext } = snapshot.stages;
  if (openingContext.state !== "wake_blocked") {
    return null;
  }
  return [
    snapshot.operationId,
    openingContext.recipientInstanceId ?? "",
    openingContext.wakeBlockedReason ?? "",
  ].join("|");
}

/**
 * Returns the toast to present for this snapshot, or null when nothing changed
 * (same block still reported) or no block is active. `key` is what the caller
 * must remember — including null, which re-arms the toast if a later snapshot
 * blocks again.
 */
export function nextWakeBlockToast(
  previousKey: string | null,
  snapshot: IssueWorkspaceLaunchResponse,
): { key: string | null; toast: ToastPayload | null } {
  const key = wakeBlockToastKey(snapshot);
  if (key === null || key === previousKey) {
    return { key, toast: null };
  }
  const presentation = wakeBlockPresentation(
    snapshot.stages.openingContext.wakeBlockedReason,
  );
  return {
    key,
    toast: {
      body: presentation.explanation,
      title: presentation.badgeLabel,
      tone: "warning",
    },
  };
}
